import { supabase } from '../../config/supabase';
import { Coordinates, ExtendedMarkerData, MarkerAction, MarkerFormData } from '../../types/marker';
import { ActionType, ActionStatus } from '../../types/action';
import { ContactCreateInput } from '../../types/contact';
import { contactsService } from './contacts.service';
import { actionsService } from './actions.service';
import logger from '../../utils/logger';
import { toDateIfString } from '../../utils/formatters';

// Interface pour les marqueurs avec GeoJSON
interface MarkerWithGeoJSON {
  id: string;
  coordinates: string; // GeoJSON sous forme de chaîne
  address: string;
  category_id: string;
  agency_id: string;
  user_id?: string | null; // ID de l'utilisateur propriétaire du marqueur
  description: string;
  image_url?: string; // URL de l'image du bien
  is_agency_marker?: boolean; // Marqueur d'agence
  created_at: string;
  updated_at: string;
}

// Définition du type RawMarkerAction pour la conversion
interface RawMarkerAction {
  id: string;
  marker_id: string;
  date: string;
  type: string;
  description: string;
  status: string;
  next_follow_up?: string;
  details?: string;
  reminder_date?: string;
  is_system?: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * Convertit une action brute (format Supabase) en action formatée pour l'application
 * @param action Action brute au format Supabase
 * @returns Action formatée pour l'application
 */
function convertRawActionToMarkerAction(action: RawMarkerAction): MarkerAction {
  return {
    id: action.id,
    markerId: action.marker_id,
    date: new Date(action.date),
    type: action.type as ActionType,
    description: action.description,
    status: action.status as ActionStatus,
    nextFollowUp: action.next_follow_up ? new Date(action.next_follow_up) : undefined,
    details: action.details,
    reminderDate: action.reminder_date ? new Date(action.reminder_date) : undefined,
    is_system: action.is_system,
    createdAt: new Date(action.created_at),
    updatedAt: new Date(action.updated_at)
  };
}

/**
 * Convertit des coordonnées en point géographique au format WKT pour Supabase
 * @param coordinates Coordonnées [longitude, latitude]
 * @returns Point géographique au format WKT
 */
function convertCoordinatesToPoint(coordinates: Coordinates): string {
  try {
    // Vérifier que les coordonnées sont valides
    if (!Array.isArray(coordinates) || coordinates.length !== 2 || 
        typeof coordinates[0] !== 'number' || typeof coordinates[1] !== 'number') {
      console.error("DIAGNOSTIC - convertCoordinatesToPoint - Coordonnées invalides:", coordinates);
      throw new Error("Coordonnées invalides");
    }
    
    // Format WKT (Well-Known Text) pour PostGIS
    const wkt = `POINT(${coordinates[0]} ${coordinates[1]})`;
    return wkt;
  } catch (error) {
    throw error;
  }
}

/**
 * Convertit un point géographique de Supabase en coordonnées
 * @param point Point géographique au format GeoJSON (chaîne ou objet), WKT ou WKB (hexadécimal)
 * @returns Coordonnées [longitude, latitude] ou null si la conversion échoue
 */
function convertPointToCoordinates(point: any): Coordinates | null {
  try {
    logger.debug("convertPointToCoordinates - Type de point reçu:", typeof point);
    
    if (!point) {
      logger.warn('Point géographique non défini');
      console.log("DIAGNOSTIC - convertPointToCoordinates - Point non défini");
      return null;
    }
    
    // Essayer chaque format dans l'ordre de priorité
    let coordinates: Coordinates | null = null;
    
    // 1. Vérifier si c'est déjà un tableau de coordonnées
    coordinates = tryParseCoordinatesArray(point);
    if (coordinates) return coordinates;
    
    // 2. Vérifier si c'est un objet GeoJSON
    coordinates = tryParseGeoJSONObject(point);
    if (coordinates) return coordinates;
    
    // 3. Vérifier si c'est une chaîne JSON (GeoJSON)
    coordinates = tryParseGeoJSONString(point);
    if (coordinates) return coordinates;
    
    // 4. Vérifier si c'est une chaîne WKT
    coordinates = tryParseWKT(point);
    if (coordinates) return coordinates;
    
    // 5. Vérifier si c'est une chaîne WKB
    coordinates = tryParseWKB(point);
    if (coordinates) return coordinates;
    
    // 6. Vérifier si c'est un objet avec x et y
    coordinates = tryParseXYObject(point);
    if (coordinates) return coordinates;
    
    // Si aucun format n'est reconnu
    logger.warn("Format de point non reconnu:", {
      pointType: typeof point,
      pointValue: typeof point === 'string' 
        ? (point as string).substring(0, 50) + '...' 
        : typeof point === 'object' && point !== null
          ? JSON.stringify(point).substring(0, 50) + '...'
          : String(point)
    });
    console.error("DIAGNOSTIC - convertPointToCoordinates - Format de point non reconnu:", {
      pointType: typeof point,
      pointValue: typeof point === 'string' 
        ? (point as string).substring(0, 50) + '...' 
        : typeof point === 'object' && point !== null
          ? JSON.stringify(point).substring(0, 50) + '...'
          : String(point)
    });
    return null;
  } catch (error) {
    logger.error("Erreur lors de la conversion du point en coordonnées:", error);
    console.error("DIAGNOSTIC - convertPointToCoordinates - Erreur lors de la conversion du point en coordonnées:", error);
    return null;
  }
}

/**
 * Essaie de parser un tableau de coordonnées
 */
function tryParseCoordinatesArray(point: any): Coordinates | null {
  if (Array.isArray(point) && point.length === 2 && 
      typeof point[0] === 'number' && typeof point[1] === 'number') {
    console.log("DIAGNOSTIC - convertPointToCoordinates - Point déjà au format [lng, lat]:", point);
    return validateCoordinates(point as Coordinates);
  }
  return null;
}

/**
 * Essaie de parser un objet GeoJSON
 */
function tryParseGeoJSONObject(point: any): Coordinates | null {
  if (typeof point === 'object' && point !== null && 
      point.type === 'Point' && Array.isArray(point.coordinates)) {
    console.log("DIAGNOSTIC - convertPointToCoordinates - Format GeoJSON (objet) détecté:", point.coordinates);
    return validateCoordinates([point.coordinates[0], point.coordinates[1]]);
  }
  return null;
}

/**
 * Essaie de parser une chaîne GeoJSON
 */
function tryParseGeoJSONString(point: any): Coordinates | null {
  if (typeof point === 'string' && 
      (point.trim().startsWith('{') || point.includes('"type":"Point"'))) {
    try {
      const geoJson = JSON.parse(point);
      console.log("DIAGNOSTIC - convertPointToCoordinates - Point parsé comme GeoJSON:", geoJson);
      
      if (geoJson.type === 'Point' && Array.isArray(geoJson.coordinates) && 
          geoJson.coordinates.length === 2) {
        console.log("DIAGNOSTIC - convertPointToCoordinates - Coordonnées extraites du GeoJSON:", geoJson.coordinates);
        return validateCoordinates([geoJson.coordinates[0], geoJson.coordinates[1]]);
      } else {
        console.log("DIAGNOSTIC - convertPointToCoordinates - GeoJSON invalide ou incomplet:", geoJson);
      }
    } catch (e) {
      console.log("DIAGNOSTIC - convertPointToCoordinates - Erreur de parsing JSON:", e);
    }
  }
  return null;
}

/**
 * Essaie de parser une chaîne WKT
 */
function tryParseWKT(point: any): Coordinates | null {
  if (typeof point === 'string') {
    const match = point.match(/POINT\(([^ ]+) ([^)]+)\)/);
    if (match) {
      const coords = [parseFloat(match[1]), parseFloat(match[2])];
      console.log("DIAGNOSTIC - convertPointToCoordinates - Format WKT détecté et parsé avec succès:", coords);
      return validateCoordinates(coords as Coordinates);
    }
  }
  return null;
}

/**
 * Essaie de parser une chaîne WKB hexadécimale
 */
function tryParseWKB(point: any): Coordinates | null {
  if (typeof point === 'string' && point.startsWith('0101000020E6100000')) {
    console.log("DIAGNOSTIC - convertPointToCoordinates - Format WKB hexadécimal détecté");
    
    try {
      // Extraire les parties hexadécimales pour X et Y
      const xHex = point.substring(18, 34);
      const yHex = point.substring(34, 50);
      
      // Convertir les valeurs hexadécimales en nombres à virgule flottante
      const xBytes = [];
      const yBytes = [];
      
      for (let i = 0; i < 16; i += 2) {
        xBytes.push(parseInt(xHex.substring(i, i + 2), 16));
        yBytes.push(parseInt(yHex.substring(i, i + 2), 16));
      }
      
      // Inverser les octets pour l'endianness
      xBytes.reverse();
      yBytes.reverse();
      
      // Créer un ArrayBuffer et une DataView pour la conversion
      const xBuffer = new ArrayBuffer(8);
      const yBuffer = new ArrayBuffer(8);
      const xView = new DataView(xBuffer);
      const yView = new DataView(yBuffer);
      
      // Remplir les vues avec les octets
      for (let i = 0; i < 8; i++) {
        xView.setUint8(i, xBytes[i]);
        yView.setUint8(i, yBytes[i]);
      }
      
      // Lire les valeurs comme des nombres à virgule flottante
      const x = xView.getFloat64(0, true);
      const y = yView.getFloat64(0, true);
      
      console.log("DIAGNOSTIC - convertPointToCoordinates - Coordonnées extraites du WKB:", [x, y]);
      return validateCoordinates([x, y]);
    } catch (e) {
      console.error("DIAGNOSTIC - convertPointToCoordinates - Erreur lors de l'extraction des coordonnées WKB:", e);
    }
  }
  return null;
}

/**
 * Essaie de parser un objet avec des propriétés x et y
 */
function tryParseXYObject(point: any): Coordinates | null {
  if (point && typeof point === 'object' && 
      point.x !== undefined && point.y !== undefined) {
    console.log("DIAGNOSTIC - convertPointToCoordinates - Format PostGIS Point détecté:", {x: point.x, y: point.y});
    return validateCoordinates([point.x, point.y]);
  }
  return null;
}

/**
 * Valide que les coordonnées sont dans des plages valides
 */
function validateCoordinates(coords: Coordinates): Coordinates | null {
  // Vérifier que les coordonnées sont des nombres valides
  if (isNaN(coords[0]) || isNaN(coords[1])) {
    logger.warn("Coordonnées invalides (NaN):", coords);
    console.log("DIAGNOSTIC - convertPointToCoordinates - Coordonnées invalides (NaN):", coords);
    return null;
  }
  
  // Vérifier que les coordonnées sont dans des plages valides
  // Longitude: -180 à 180, Latitude: -90 à 90
  if (coords[0] < -180 || coords[0] > 180 || coords[1] < -90 || coords[1] > 90) {
    logger.warn("Coordonnées hors limites:", coords);
    console.log("DIAGNOSTIC - convertPointToCoordinates - Coordonnées hors limites:", coords);
    // On pourrait décider de les retourner quand même avec un avertissement
    // ou de les ajuster aux limites valides
  }
  
  return coords;
}

/**
 * Prépare les données d'un marqueur pour Supabase
 * @param markerData Données du marqueur
 * @param fields Champs à inclure (optionnel)
 * @returns Données formatées pour Supabase
 */
function prepareMarkerForSupabase(markerData: MarkerFormData | Partial<ExtendedMarkerData>, fields: string[] = []): any {
  const data: Record<string, any> = {};
  
  // Conversion des coordonnées en point géographique
  if (markerData.coordinates) {
    data.coordinates = convertCoordinatesToPoint(markerData.coordinates);
  }
  
  // Copie des champs standards
  if (markerData.address) data.address = markerData.address;
  if (markerData.categoryId !== undefined) data.category_id = markerData.categoryId;
  if (markerData.agency_id !== undefined) data.agency_id = markerData.agency_id;
  if (markerData.description !== undefined) data.description = markerData.description;
  
  // IMPORTANT: Inclure le user_id s'il est défini
  // Cette modification est essentielle pour que les marqueurs créés soient correctement associés à leur utilisateur
  // et apparaissent dans le bon filtre
  if ('user_id' in markerData && markerData.user_id !== undefined) {
    data.user_id = markerData.user_id;
    if (process.env.NODE_ENV === 'development') {
      logger.debug('user_id inclus dans les données du marqueur:', markerData.user_id);
    }
  }
  
  // Gestion explicite de imageUrl pour permettre la suppression (null vs undefined)
  if ('imageUrl' in markerData) {
    // Forcer null si undefined pour que Supabase supprime l'image
    data.image_url = markerData.imageUrl || null;
    console.log('🖼️ DEBUG prepareMarkerForSupabase - imageUrl:', {
      hasImageUrl: !!markerData.imageUrl,
      imageUrlType: typeof markerData.imageUrl,
      finalValue: data.image_url === null ? 'NULL' : 'VALEUR',
      timestamp: new Date().toISOString()
    });
  }

  // Champs spécifiques si demandés
  if (fields.includes('description') && markerData.description) {
    data.description = markerData.description;
  }
  
  return data;
}

/**
 * Gère tous les contacts associés à un marqueur (création, mise à jour, suppression)
 * @param id ID du marqueur
 * @param contactData Données contenant le tableau de contacts
 * @returns Contacts créés ou mis à jour
 */
async function handleMarkerContacts(id: string, contactData: any) {
  try {
    // Récupérer les contacts existants pour ce marqueur (triés par date de création)
    const { data: existingContacts, error: fetchError } = await supabase
      .from('contacts')
      .select('id, marker_id, gender, name, first_name, address, postal_code, city, phone, email, notes, designation, created_at')
      .eq('marker_id', id)
      .order('created_at', { ascending: true });
    
    if (fetchError) {
      logger.error('Erreur lors de la récupération des contacts existants:', fetchError);
      throw fetchError;
    }
    
    const newContacts = contactData.contacts || [];
    
    logger.debug('📋 handleMarkerContacts - Traitement des contacts:', {
      existingCount: existingContacts?.length || 0,
      newCount: newContacts.length
    });
    
    // Map des contacts existants par ID
    const existingContactsMap = new Map(existingContacts?.map(c => [c.id, c]) || []);
    
    // Set des IDs des nouveaux contacts (pour identifier ceux à supprimer)
    const newContactIds = new Set(newContacts.map((c: any) => c.id).filter((id: any) => id));
    
    const processedContacts = [];
    
    // Traiter chaque contact du tableau
    for (let i = 0; i < newContacts.length; i++) {
      const contactItem = newContacts[i];
      
      const contact: any = {
        marker_id: id,
        gender: contactItem.gender,
        name: contactItem.name || '',
        first_name: contactItem.firstName || '',
        address: contactItem.address || '',
        postal_code: contactItem.postalCode || '',
        city: contactItem.city || '',
        phone: contactItem.phone || '',
        email: contactItem.email || '',
        notes: contactItem.notes || '',
        designation: contactItem.designation || 'contact'
      };
      
      // Si le contact a un ID et existe en base, le mettre à jour
      if (contactItem.id && existingContactsMap.has(contactItem.id)) {
        const { data, error } = await supabase
          .from('contacts')
          .update(contact)
          .eq('id', contactItem.id)
          .select()
          .single();
        
        if (error) {
          logger.error(`Erreur lors de la mise à jour du contact ${i}:`, error);
          throw error;
        }
        
        processedContacts.push(data);
        logger.debug(`✅ Contact ${i} mis à jour:`, { id: data.id, name: data.name });
      } 
      // Sinon, créer un nouveau contact
      else {
        const { data, error } = await supabase
          .from('contacts')
          .insert(contact)
          .select()
          .single();
        
        if (error) {
          logger.error(`Erreur lors de la création du contact ${i}:`, error);
          throw error;
        }
        
        processedContacts.push(data);
        logger.debug(`✅ Contact ${i} créé:`, { id: data.id, name: data.name });
      }
    }
    
    // Supprimer les contacts qui ne sont plus dans le tableau newContacts
    if (existingContacts) {
      for (const existingContact of existingContacts) {
        if (!newContactIds.has(existingContact.id)) {
          logger.debug(`🔍 Tentative de suppression du contact:`, { 
            id: existingContact.id, 
            name: existingContact.name,
            isInNewContacts: newContactIds.has(existingContact.id)
          });
          
          const { data: deletedData, error } = await supabase
            .from('contacts')
            .delete()
            .eq('id', existingContact.id)
            .select();
          
          if (error) {
            logger.error(`❌ Erreur lors de la suppression du contact:`, error);
          } else if (!deletedData || deletedData.length === 0) {
            logger.error(`❌ Aucun contact supprimé (RLS ou permissions?):`, { 
              id: existingContact.id, 
              name: existingContact.name,
              deletedData 
            });
          } else {
            logger.debug(`✅ Contact supprimé avec succès:`, { 
              id: existingContact.id, 
              name: existingContact.name,
              deletedCount: deletedData.length 
            });
          }
        }
      }
    }
    
    logger.debug(`📋 Résumé final:`, {
      contactsTraites: processedContacts.length,
      newContactIds: Array.from(newContactIds),
      existingContactIds: existingContacts?.map(c => c.id) || []
    });
    
    logger.debug(`📋 Traitement terminé: ${processedContacts.length} contacts traités`);
    
    return processedContacts;
  } catch (error: any) {
    logger.error('Exception lors de la gestion des contacts:', error);
    throw error;
  }
}

export const markersService = {
  /**
   * Récupère tous les marqueurs
   * @returns Liste des marqueurs
   */
  /**
   * Récupère tous les marqueurs, avec possibilité de filtrer par utilisateur
   * @param filterUserId ID de l'utilisateur pour filtrer les marqueurs (optionnel)
   * @returns Liste des marqueurs
   */
  async getAll(filterUserId?: string | null): Promise<ExtendedMarkerData[]> {
    try {
      console.log("DIAGNOSTIC - Début de récupération des marqueurs depuis Supabase");
      
      // Récupérer l'utilisateur courant
      const { data: userData } = await supabase.auth.getUser();
      if (!userData || !userData.user) {
        logger.error('Utilisateur non authentifié');
        return [];
      }
      
      // Récupérer le profil utilisateur pour vérifier s'il est admin/propriétaire
      const { data: userProfile } = await supabase
        .from('users')
        .select('is_owner, role')
        .eq('id', userData.user.id)
        .single();
      
      const isAdmin = userProfile?.is_owner || userProfile?.role === 'admin';
      logger.info(`Utilisateur ${userData.user.id} est ${isAdmin ? 'admin/propriétaire' : 'utilisateur standard'}`);
      
      // Récupérer les marqueurs avec les coordonnées au format GeoJSON
      let query;
      
      if (isAdmin) {
        // Si un filtre d'utilisateur est spécifié et que l'utilisateur est admin
        if (filterUserId) {
          logger.info(`Admin filtre les marqueurs pour l'utilisateur: ${filterUserId}`);
          query = supabase.rpc('get_markers_with_geojson_by_user', { user_id_param: filterUserId });
        } else {
          // Les administrateurs peuvent voir tous les marqueurs de l'agence
          query = supabase.rpc('get_markers_with_geojson');
        }
      } else {
        // Les utilisateurs standards ne peuvent voir que leurs propres marqueurs
        query = supabase.rpc('get_markers_with_geojson_by_user', { user_id_param: userData.user.id });
      }
      
      const { data: markersDataRaw, error: markersError } = await query;
      
      if (markersError) {
        console.error("DIAGNOSTIC - ERREUR lors de la récupération des marqueurs:", markersError);
        throw markersError;
      }
      
      // Convertir les données brutes en tableau de MarkerWithGeoJSON
      const markersData = markersDataRaw as MarkerWithGeoJSON[];
      
      console.log("DIAGNOSTIC - Marqueurs récupérés:", markersData?.length || 0, "marqueurs");
      if (markersData && markersData.length > 0) {
        console.log("DIAGNOSTIC - Premier marqueur brut:", JSON.stringify(markersData[0], null, 2));
      
      // Vérifier si les marqueurs ont un user_id
      const markersWithUserId = markersData.filter(m => m.user_id);
      console.log(`👤 DIAGNOSTIC - Marqueurs avec user_id: ${markersWithUserId.length}/${markersData.length}`);
      
      if (markersWithUserId.length > 0) {
        console.log("👤 DIAGNOSTIC - Exemple de marqueur avec user_id:", {
          id: markersWithUserId[0].id,
          user_id: markersWithUserId[0].user_id,
          address: markersWithUserId[0].address
        });
      } else {
        console.warn("⚠️ DIAGNOSTIC - Aucun marqueur n'a de user_id!");
      }
      } else {
        console.log("DIAGNOSTIC - Aucun marqueur trouvé dans la base de données");
        return [];
      }
      
      // Récupérer les contacts pour tous les marqueurs
      const { data: contactsData, error: contactsError } = await supabase
        .from('contacts')
        .select('id, marker_id, gender, name, first_name, address, postal_code, city, phone, email, notes, designation, created_at, updated_at');
      
      if (contactsError) {
        logger.error('Erreur lors de la récupération des contacts:', contactsError);
        throw contactsError;
      }
      
      // Récupérer les actions pour tous les marqueurs
      const { data: actionsData, error: actionsError } = await supabase
        .from('actions')
        .select('id, marker_id, date, type, description, status, user_id, next_follow_up, details, reminder_date, is_system, created_at, updated_at');
      
      if (actionsError) {
        logger.error('Erreur lors de la récupération des actions:', actionsError);
        throw actionsError;
      }
      
      // Organiser les contacts et actions par markerId
      const contactsByMarkerId: Record<string, any[]> = {};
      const actionsByMarkerId: Record<string, RawMarkerAction[]> = {};
      
      contactsData?.forEach(contact => {
        if (!contactsByMarkerId[contact.marker_id]) {
          contactsByMarkerId[contact.marker_id] = [];
        }
        contactsByMarkerId[contact.marker_id].push(contact);
      });
      
      actionsData?.forEach(action => {
        if (!actionsByMarkerId[action.marker_id]) {
          actionsByMarkerId[action.marker_id] = [];
        }
        actionsByMarkerId[action.marker_id].push(action);
      });
      
      // Construire les ExtendedMarkerData
      const extendedMarkers: ExtendedMarkerData[] = [];
      
      for (const marker of markersData) {
        try {
          // Traiter les coordonnées
          let coordinates: Coordinates | null = null;
          
          // Vérifier le format des coordonnées
          console.log("DIAGNOSTIC - Coordonnées brutes du marqueur:", {
            id: marker.id,
            coordinates: marker.coordinates,
            type: typeof marker.coordinates
          });
          
          // Convertir les coordonnées depuis le format GeoJSON
          coordinates = convertPointToCoordinates(marker.coordinates);
          console.log("DIAGNOSTIC - Coordonnées converties depuis GeoJSON:", coordinates);
          
          // Si les coordonnées sont invalides, ignorer ce marqueur
          if (coordinates === null) {
            console.warn(`DIAGNOSTIC - Marqueur ${marker.id} ignoré car coordonnées invalides:`, { 
              markerId: marker.id,
              coordinates: marker.coordinates
            });
            continue;
          }
          
          // Récupérer le contact principal
          const contacts = contactsByMarkerId[marker.id] || [];
          const primaryContact = contacts && contacts.length > 0 ? {
            id: contacts[0].id,
            gender: contacts[0].gender,
            name: contacts[0].name || '',
            firstName: contacts[0].first_name || '',
            type: 'other' as 'owner' | 'agent' | 'other', // Type par défaut
            address: contacts[0].address || '',
            postalCode: contacts[0].postal_code || '',
            city: contacts[0].city || '',
            phone: contacts[0].phone || '',
            email: contacts[0].email || '',
            notes: contacts[0].notes || '',
            designation: contacts[0].designation || 'contact'
          } : {
            id: '',
            name: '',
            firstName: '',
            type: 'other' as 'owner' | 'agent' | 'other', // Type par défaut
            address: '',
            postalCode: '',
            city: '',
            phone: '',
            email: '',
            notes: '',
            designation: 'contact' as any
          };
          
          // Convertir les actions
          const actions = (actionsByMarkerId[marker.id] || [])
            .map(convertRawActionToMarkerAction)
            .sort((a, b) => b.date.getTime() - a.date.getTime());
          
          // Formater tous les contacts
          const formattedContacts = contacts.map(contact => ({
            id: contact.id,
            gender: contact.gender,
            name: contact.name || '',
            firstName: contact.first_name || '',
            type: 'other' as 'owner' | 'agent' | 'other',
            address: contact.address || '',
            postalCode: contact.postal_code || '',
            city: contact.city || '',
            phone: contact.phone || '',
            email: contact.email || '',
            notes: contact.notes || '',
            designation: contact.designation || 'contact'
          }));
          
          // Construire l'objet ExtendedMarkerData
          const extendedMarker: ExtendedMarkerData = {
            id: marker.id,
            coordinates,
            address: marker.address,
            categoryId: marker.category_id,
            agency_id: marker.agency_id,
            user_id: marker.user_id || null,
            is_agency_marker: marker.is_agency_marker,
            createdAt: new Date(marker.created_at),
            updatedAt: new Date(marker.updated_at),
            description: marker.description,
            imageUrl: marker.image_url,
            name: primaryContact.name || '',
            firstName: primaryContact.firstName || '',
            contact_address: primaryContact.address || '',
            phone: primaryContact.phone || '',
            email: primaryContact.email || '',
            notes: primaryContact.notes || '',
            contacts: formattedContacts,
            actions,
            status: 'active'
          };
          
          console.log("DIAGNOSTIC - Marqueur traité avec succès:", {
            id: extendedMarker.id,
            coordinates: extendedMarker.coordinates,
            categoryId: extendedMarker.categoryId,
            hasImageUrl: !!extendedMarker.imageUrl,
            imageUrlLength: extendedMarker.imageUrl?.length || 0
          });
          
          extendedMarkers.push(extendedMarker);
        } catch (error) {
          console.error(`DIAGNOSTIC - Erreur lors du traitement du marqueur ${marker.id}:`, error);
          // Continuer avec le marqueur suivant
        }
      }
      
      console.log("DIAGNOSTIC - Total des marqueurs traités:", extendedMarkers.length);
      return extendedMarkers;
    } catch (error: any) {
      logger.error('Exception lors de la récupération des marqueurs:', error);
      throw error;
    }
  },
  
  /**
   * Récupère un marqueur par son ID
   * @param id ID du marqueur
   * @returns Marqueur ou null si non trouvé
   */
  async getById(id: string): Promise<ExtendedMarkerData | null> {
    try {
      // Récupérer le marqueur depuis Supabase
      const { data: markerData, error: markerError } = await supabase
        .rpc('get_marker_with_geojson', { marker_id: id })
        .single();
      
      if (markerError) {
        if (markerError.code === 'PGRST116') {
          // Marqueur non trouvé
          return null;
        }
        logger.error('Erreur lors de la récupération du marqueur:', markerError);
        throw markerError;
      }
      
      // Convertir les données en objet MarkerWithGeoJSON
      const marker = markerData as MarkerWithGeoJSON;
      
      // Convertir les coordonnées
      const coordinates = convertPointToCoordinates(marker.coordinates);
      
      // Si les coordonnées sont invalides, logger et retourner null
      if (coordinates === null) {
        logger.error(`Marqueur ${id} ignoré car coordonnées invalides:`, { 
          markerId: id,
          rawCoordinates: marker.coordinates
        });
        return null;
      }
      
      // Récupérer les contacts pour ce marqueur (triés par date de création)
      const { data: contacts, error: contactsError } = await supabase
        .from('contacts')
        .select('id, marker_id, gender, name, first_name, address, postal_code, city, phone, email, notes, designation, created_at, updated_at')
        .eq('marker_id', id)
        .order('created_at', { ascending: true });
      
      if (contactsError) {
        logger.error('Erreur lors de la récupération des contacts:', contactsError);
        throw contactsError;
      }
      
      // Convertir les contacts au format de l'application
      const formattedContacts = contacts ? contacts.map(contact => ({
        id: contact.id,
        gender: contact.gender,
        name: contact.name || '',
        firstName: contact.first_name || '',
        type: 'other' as 'owner' | 'agent' | 'other', // Type par défaut
        address: contact.address || '',
        postalCode: contact.postal_code || '',
        city: contact.city || '',
        phone: contact.phone || '',
        email: contact.email || '',
        notes: contact.notes || '',
        designation: contact.designation || 'contact'
      })) : [];
      
      // Récupérer les actions pour ce marqueur
      const { data: actions, error: actionsError } = await supabase
        .from('actions')
        .select('id, marker_id, date, type, description, status, user_id, next_follow_up, details, reminder_date, is_system, created_at, updated_at')
        .eq('marker_id', id);
      
      if (actionsError) {
        logger.error('Erreur lors de la récupération des actions:', actionsError);
        throw actionsError;
      }
      
      // Récupérer le contact principal
      const primaryContact = contacts && contacts.length > 0 ? {
        id: contacts[0].id,
        gender: contacts[0].gender,
        name: contacts[0].name || '',
        firstName: contacts[0].first_name || '',
        type: 'other' as 'owner' | 'agent' | 'other', // Type par défaut
        address: contacts[0].address || '',
        phone: contacts[0].phone || '',
        email: contacts[0].email || '',
        notes: contacts[0].notes || '',
        designation: contacts[0].designation || 'contact'
      } : {
        id: '',
        name: '',
        firstName: '',
        type: 'other' as 'owner' | 'agent' | 'other', // Type par défaut
        address: '',
        phone: '',
        email: '',
        notes: '',
        designation: 'contact' as any
      };
      
      // Convertir les actions
      const markerActions = actions
        ? actions.map(convertRawActionToMarkerAction).sort((a, b) => b.date.getTime() - a.date.getTime())
        : [];
      
      // Construire l'objet ExtendedMarkerData
      const extendedMarker: ExtendedMarkerData = {
        id: marker.id,
        coordinates,
        address: marker.address,
        categoryId: marker.category_id,
        agency_id: marker.agency_id,
        createdAt: new Date(marker.created_at),
        updatedAt: new Date(marker.updated_at),
        description: marker.description,
        imageUrl: marker.image_url || undefined,
        name: primaryContact.name || '',
        firstName: primaryContact.firstName || '',
        contact_address: primaryContact.address || '',
        phone: primaryContact.phone || '',
        email: primaryContact.email || '',
        notes: primaryContact.notes || '',
        contacts: formattedContacts,
        actions: markerActions,
        status: 'active'
      };
      
      // Debug spécifique pour l'imageUrl
      console.log('🖼️ DEBUG getById - imageUrl:', {
        markerId: marker.id,
        rawImageUrl: marker.image_url,
        hasRawImageUrl: !!marker.image_url,
        finalImageUrl: extendedMarker.imageUrl,
        hasFinalImageUrl: !!extendedMarker.imageUrl,
        imageUrlType: typeof extendedMarker.imageUrl,
        imageUrlLength: extendedMarker.imageUrl?.length || 0
      });
      
      return extendedMarker;
    } catch (error: any) {
      logger.error('Exception lors de la récupération du marqueur:', error);
      throw error;
    }
  },
  
  /**
   * Crée un nouveau marqueur
   * @param markerData Données du marqueur
   * @returns Marqueur créé
   */
  async create(markerData: MarkerFormData): Promise<ExtendedMarkerData> {
    try {
      // Préparer les données pour Supabase sans les coordonnées
      const markerDataForSupabase = prepareMarkerForSupabase(markerData, ['coordinates']);
      
      // Récupérer l'utilisateur actuel et son profil
      const { data: authData } = await supabase.auth.getSession();
      const currentUser = authData.session?.user;
      
      // Si l'utilisateur est connecté, utiliser son ID et récupérer son agency_id
      if (currentUser) {
        // Ajouter l'ID de l'utilisateur
        markerDataForSupabase.user_id = currentUser.id;
        
        // Récupérer le profil utilisateur pour obtenir l'agency_id
        if (!markerData.agency_id) {
          const { data: userProfile } = await supabase
            .from('users')
            .select('agency_id')
            .eq('id', currentUser.id)
            .single();
          
          if (userProfile && userProfile.agency_id) {
            markerDataForSupabase.agency_id = userProfile.agency_id;
          }
        }
      }
      
      // Utiliser l'agency_id fourni si présent
      if (markerData.agency_id) {
        markerDataForSupabase.agency_id = markerData.agency_id;
      }
      
      // Créer le WKT pour les coordonnées
      const wkt = convertCoordinatesToPoint(markerData.coordinates);
      
      // Utiliser la méthode d'insertion standard de Supabase mais avec des coordonnées temporaires
      markerDataForSupabase.coordinates = null; // Valeur temporaire
      
      const { data: tempMarker, error: insertError } = await supabase
        .from('markers')
        .insert(markerDataForSupabase)
        .select()
        .single();
      
      if (insertError) {
        logger.error('Erreur lors de la création du marqueur:', insertError);
        throw insertError;
      }
      
      if (!tempMarker) {
        throw new Error('Le marqueur n\'a pas été créé');
      }
      
      // Maintenant, mettre à jour les coordonnées avec une requête SQL brute
      const updateQuery = `
        WITH updated AS (
          UPDATE markers 
          SET coordinates = ST_GeomFromText('${wkt}', 4326)::geography
          WHERE id = '${tempMarker.id}'
          RETURNING id, ST_AsGeoJSON(coordinates) AS coordinates, name, address, category_id, user_id, agency_id, created_at, updated_at
        )
        SELECT row_to_json(updated) FROM updated;
      `;
      
      logger.debug('Exécution de la requête SQL pour mettre à jour les coordonnées:', updateQuery);
      
      const { data: updatedMarkers, error: updateError } = await supabase.rpc(
        'run_sql_query',
        { sql_query: updateQuery }
      );
      
      if (updateError) {
        logger.error('Erreur lors de la mise à jour des coordonnées:', updateError);
        logger.error('Détails de l\'erreur:', JSON.stringify(updateError));
        // Continuer avec le marqueur temporaire même si la mise à jour des coordonnées a échoué
      } else {
        logger.debug('Résultat de la mise à jour des coordonnées:', updatedMarkers);
      }
      
      // Utiliser le marqueur mis à jour si disponible, sinon utiliser le marqueur temporaire
      const marker = (updatedMarkers && Array.isArray(updatedMarkers) && updatedMarkers.length > 0) 
        ? updatedMarkers[0].row_to_json || updatedMarkers[0] 
        : tempMarker;
      
      logger.debug('Marqueur final utilisé:', marker);
      
      // Construire l'objet ExtendedMarkerData
      const coordinates = convertPointToCoordinates(marker.coordinates);
      
      if (!coordinates) {
        logger.error('Les coordonnées du marqueur créé sont invalides', { 
          markerId: marker.id,
          rawCoordinates: marker.coordinates
        });
        throw new Error('Les coordonnées du marqueur créé sont invalides');
      }
      
      const extendedMarker: ExtendedMarkerData = {
        id: marker.id,
        coordinates,
        address: marker.address,
        categoryId: marker.category_id,
        agency_id: marker.agency_id,
        createdAt: new Date(marker.created_at),
        updatedAt: new Date(marker.updated_at),
        description: marker.description,
        imageUrl: marker.image_url,
        name: '',
        firstName: '',
        contact_address: '',
        phone: '',
        email: '',
        notes: '',
        actions: [],
        status: 'active'
      };

      // Créer un contact associé au marqueur avec toutes les informations de contact
      try {
        const contactData: ContactCreateInput = {
          markerId: marker.id,
          name: markerData.name || '',
          firstName: markerData.firstName || '',
          address: markerData.contact_address || '',
          phone: markerData.phone || '',
          email: markerData.email || '',
          notes: markerData.notes || ''
        };
        
        await contactsService.create(contactData);
      } catch (contactError: any) {
        logger.error('Erreur lors de la création du contact:', contactError);
      }

      const actions: MarkerAction[] = [];
      if (markerData.actions && markerData.actions.length > 0) {
        try {
          logger.debug(`Traitement de ${markerData.actions.length} actions pour le marqueur ${marker.id}`);
          
          // Utiliser actionsService.create pour chaque action afin de migrer les alertes temporaires
          for (const action of markerData.actions) {
            try {
              // Préserver l'ID temporaire s'il existe
              const actionWithId = action as any; // Utiliser any pour éviter les erreurs de typage
              const tempId = typeof actionWithId.id === 'string' && actionWithId.id.startsWith('temp_') ? actionWithId.id : undefined;
              
              logger.debug(`Action à créer:`, {
                actionData: action,
                hasTemporaryId: !!tempId,
                temporaryId: tempId
              });
              
              // Créer un objet avec les propriétés au format attendu par actionsService.create
              const newActionData: any = {
                markerId: marker.id,
                type: action.type as ActionType,
                description: action.description,
                status: action.status as ActionStatus || 'pending',
                date: new Date(action.date), // Convertir en Date
                details: action.details,
                is_system: action.is_system ?? false,
                user_id: action.user_id
              };
              
              // Ajouter les propriétés optionnelles si elles existent
              if (action.next_follow_up) {
                newActionData.nextFollowUp = toDateIfString(action.next_follow_up);
              }
              
              if (action.reminder_date) {
                newActionData.reminderDate = toDateIfString(action.reminder_date);
              }
              
              // Ajouter l'ID temporaire si présent
              if (tempId) {
                newActionData.tempId = tempId;
              }
              
              const newAction = await actionsService.create(newActionData);
              
              logger.debug(`Nouvelle action créée avec succès:`, {
                oldId: tempId,
                newId: newAction.id,
                description: newAction.description
              });
              
              // Confirmer les actions temporaires immédiatement après leur création
              if (newAction.isTemporary) {
                await actionsService.confirmTemporaryActions(marker.id);
                logger.info(`Actions temporaires confirmées pour le marqueur ${marker.id}`);
              }
              
              actions.push(newAction);
            } catch (actionError: any) {
              logger.error('Erreur lors de la création d\'une action:', actionError);
            }
          }
        } catch (actionsError: any) {
          logger.error('Erreur lors du traitement des actions:', actionsError);
        }
      }

      extendedMarker.actions = actions;

      return extendedMarker;
    } catch (error: any) {
      logger.error('Exception lors de la création du marqueur:', error);
      throw error;
    }
  },
  
  /**
   * Met à jour un marqueur existant
   * @param id ID du marqueur
   * @param markerData Données du marqueur
   * @returns Marqueur mis à jour
   */
  async update(id: string, markerData: MarkerFormData): Promise<ExtendedMarkerData> {
    try {
      logger.debug('Mise à jour du marqueur:', {
        id,
        address: markerData.address,
        categoryId: markerData.categoryId,
        coordinates: markerData.coordinates
      });
      
      // Préparer les données pour Supabase
      const data = prepareMarkerForSupabase(markerData);
      
      // Mettre à jour le marqueur dans Supabase
      const { error } = await supabase
        .from('markers')
        .update(data)
        .eq('id', id);
      
      if (error) {
        logger.error('Erreur lors de la mise à jour du marqueur:', error);
        throw error;
      }
      
      // Mettre à jour les contacts associés au marqueur
      try {
        logger.debug('📋 update - Données contacts avant handleMarkerContacts:', {
          hasContacts: !!markerData.contacts,
          contactsLength: markerData.contacts?.length || 0
        });
        
        await handleMarkerContacts(id, markerData);
      } catch (contactError: any) {
        logger.error('Erreur lors de la mise à jour des contacts:', contactError);
      }
      
      // Récupérer les actions existantes pour ce marqueur
      const existingActions = await actionsService.getByMarkerId(id);
      
      // Créer un Map des actions existantes pour faciliter la recherche
      const existingActionsMap = new Map<string, MarkerAction>();
      existingActions.forEach(action => {
        existingActionsMap.set(action.id, action);
      });
      
      // Actions à traiter
      const actions = markerData.actions || [];
      
      // Traiter les actions si elles existent
      if (actions.length > 0) {
        logger.debug(`Traitement de ${actions.length} actions pour le marqueur ${id} `);
        
        for (const markerAction of actions) {
          try {
            // Vérifier si l'action a un ID et s'il est temporaire
            // Utiliser le type casting pour éviter les erreurs de typage
            const actionId = (markerAction as any).id;
            const isTemporaryId = typeof actionId === 'string' && actionId.startsWith('temp_');
            
            logger.debug(`Traitement de l'action:`, {
              actionId,
              hasTemporaryId: isTemporaryId,
              description: markerAction.description
            });
            
            if (isTemporaryId || !actionId) {
              // C'est une nouvelle action, la créer
              const tempId = actionId;
              
              // Créer un objet avec les propriétés au format attendu par actionsService.create
              const newActionData: any = {
                markerId: id,
                type: markerAction.type as ActionType,
                description: markerAction.description,
                status: markerAction.status as ActionStatus || 'pending',
                date: new Date(markerAction.date), // Convertir en Date
                details: markerAction.details,
                is_system: markerAction.is_system ?? false,
                user_id: markerAction.user_id
              };
              
              // Ajouter les propriétés optionnelles si elles existent
              if (markerAction.next_follow_up) {
                newActionData.nextFollowUp = toDateIfString(markerAction.next_follow_up);
              }
              
              if (markerAction.reminder_date) {
                newActionData.reminderDate = toDateIfString(markerAction.reminder_date);
              }
              
              // Ajouter l'ID temporaire si présent
              if (tempId) {
                newActionData.tempId = tempId;
              }
              
              const newAction = await actionsService.create(newActionData);
              
              logger.debug(`Nouvelle action créée avec succès:`, {
                oldId: tempId,
                newId: newAction.id,
                description: newAction.description
              });
            } else {
              // C'est une action existante, la mettre à jour
              // Créer un objet avec les propriétés correctes pour éviter les erreurs de typage
              const actionData: any = {
                markerId: id,
                type: markerAction.type as ActionType,
                description: markerAction.description,
                status: markerAction.status as ActionStatus || 'pending',
                date: markerAction.date,
                details: markerAction.details,
                is_system: markerAction.is_system ?? false,
                user_id: markerAction.user_id
              };
              
              // Ajouter les propriétés optionnelles si elles existent
              if (markerAction.next_follow_up) {
                actionData.nextFollowUp = toDateIfString(markerAction.next_follow_up);
              }
              
              if (markerAction.reminder_date) {
                actionData.reminderDate = toDateIfString(markerAction.reminder_date);
              }
              
              try {
                const updatedAction = await actionsService.update(actionId, actionData);
                
                logger.debug(`Action existante mise à jour:`, {
                  actionId: updatedAction.id,
                  description: updatedAction.description
                });
              } catch (updateError: any) {
                // Si l'action n'existe pas, on la crée
                if (updateError.message && updateError.message.includes("n'existe pas")) {
                  logger.info(`L'action ${actionId} n'existe pas, création d'une nouvelle action à la place`);
                  try {
                    // Supprimer l'ID de actionData pour créer une nouvelle action
                    const newActionData = { ...actionData };
                    const newAction = await actionsService.create(newActionData);
                    
                    logger.debug(`Nouvelle action créée en remplacement:`, {
                      oldId: actionId,
                      newId: newAction.id,
                      description: newAction.description
                    });
                  } catch (createError: any) {
                    logger.error('Erreur lors de la création de l\'action de remplacement:', createError);
                  }
                } else {
                  logger.error('Erreur lors de la mise à jour de l\'action:', updateError);
                }
              }
            }
          } catch (actionError: any) {
            logger.error('Erreur lors du traitement de l\'action:', actionError);
          }
        }
      }
      
      // Attendre un peu pour que la transaction de suppression soit commitée
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Récupérer le marqueur complet avec toutes ses relations
      const extendedMarker = await this.getById(id);
      
      if (!extendedMarker) {
        throw new Error('Le marqueur mis à jour n\'a pas pu être récupéré');
      }
      
      // Assurons-nous que le prénom est correctement récupéré
      logger.debug('Marqueur récupéré après mise à jour:', {
        id: extendedMarker.id,
        name: extendedMarker.name,
        firstName: extendedMarker.firstName,
        contacts: extendedMarker.contacts ? extendedMarker.contacts.length : 0
      });
      
      return extendedMarker;
    } catch (error: any) {
      logger.error('Exception lors de la mise à jour du marqueur:', error);
      throw error;
    }
  },
  
  /**
   * Supprime un marqueur
   * @param id ID du marqueur
   */
  async delete(id: string): Promise<void> {
    try {
      // Vérifier si le marqueur existe
      const existingMarker = await this.getById(id);
      
      if (!existingMarker) {
        throw new Error(`Le marqueur avec l'ID ${id} n'existe pas`);
      }
      
      // Supprimer les contacts associés
      const { error: contactsError } = await supabase
        .from('contacts')
        .delete()
        .eq('marker_id', id);
      
      if (contactsError) {
        logger.error('Erreur lors de la suppression des contacts:', contactsError);
        throw contactsError;
      }
      
      // Supprimer les actions associées
      const { error: actionsError } = await supabase
        .from('actions')
        .delete()
        .eq('marker_id', id);
      
      if (actionsError) {
        logger.error('Erreur lors de la suppression des actions:', actionsError);
        throw actionsError;
      }
      
      // Supprimer le marqueur
      const { error } = await supabase
        .from('markers')
        .delete()
        .eq('id', id);
      
      if (error) {
        logger.error('Erreur lors de la suppression du marqueur:', error);
        throw error;
      }
      
      logger.info(`Marqueur ${id} supprimé avec succès`);
    } catch (error: any) {
      logger.error('Exception lors de la suppression du marqueur:', error);
      throw error;
    }
  }
};

